import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';
import Logger from 'utils/logger';
import { InstagramAudioApi } from '../api/instagramAudio';
import type { InstagramAudioPage, InstagramAudioTypeFilter } from '../types';

type Params = {
  pageId?: number;
  audioType: InstagramAudioTypeFilter;
  searchQuery: string;
  enabled?: boolean;
};

export function useInstagramAudioTracksQuery({
  pageId,
  audioType,
  searchQuery,
  enabled = true,
}: Params) {
  const tracksQuery = useInfiniteQuery<InstagramAudioPage, ApiClientError>(
    queryKey.instagramAudioTracks({
      pageId: pageId ?? 0,
      audioType,
      searchQuery,
    }),
    ({ pageParam }) =>
      InstagramAudioApi.list({
        pageId: pageId ?? 0,
        audioType: audioType === 'all' ? undefined : audioType,
        searchQuery: searchQuery || undefined,
        cursor: pageParam,
      }),
    {
      enabled: enabled && !!pageId,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      onError: (apiError) => Logger.error(apiError),
    },
  );

  const tracks = useMemo(
    () => tracksQuery.data?.pages.flatMap((page) => page.tracks) ?? [],
    [tracksQuery.data],
  );

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: tracksQuery.isLoading || tracksQuery.isFetchingNextPage,
    hasNextPage: !!tracksQuery.hasNextPage,
    disabled: tracksQuery.isError,
    onLoadMore: () => tracksQuery.fetchNextPage(),
  });

  return {
    tracks,
    isLoading: tracksQuery.isLoading,
    isError: tracksQuery.isError,
    error: tracksQuery.error ?? null,
    hasNextPage: !!tracksQuery.hasNextPage,
    isFetchingNextPage: tracksQuery.isFetchingNextPage,
    refetch: tracksQuery.refetch,
    sentryRef,
    rootRef,
  };
}
